import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getBuildingsDetailsAsync } from "src/api/building/building-api";
import { BuildingModel } from "src/api/building/building-types";
import { getQuotationList, getRefundInfo, patchContractCancelAsync } from "src/api/contract/contract-api";
import { changeStatusText, ContractModel, Quotation } from "src/api/contract/contract-types";
import { getPublicMediaFilesAsync } from "src/api/file/file-api";
import { MediaFile } from "src/api/file/file-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { getProductAsync } from "src/api/product/product-api";
import { ProductModel } from "src/api/product/product-types";
import { Address } from "src/api/public-types";
import noimg from "src/assets/images/icons_large_noimg.svg";
import { BaseButton, ConfirmModal, ResizedImage } from "src/components";
import BaseFullModal from "src/components/BaseFullModal";
import Tooltip from "src/pages/components/Tooltip";
import { useMemberInfoState } from "src/recoil/member/hook";
import getAppInfo from "src/utils/app-Info";
import { formatDateTimeRange, makeCommonSpaceList, ViewYmdFormat } from "src/utils/common-util";
import ContractCancel from "./ContractCancel";

// 컴포넌트 Props 타입 정의
type Props = {
  contract?: ContractModel | null; // 계약 정보
  fetchContract: (contractId: string) => void; // 계약 정보 조회 함수
  isContractor?: boolean;
  setPrimaryQuotationName?: any; // 주요 견적서 이름 설정 함수
};

// 모달 상태 타입 정의
type Modal = {
  isOpen: boolean; // 모달 표시 여부
  message?: string; // 모달 메시지
  type?: string; // 모달 타입
};

/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 화면 > 기본 정보
 * 계약의 기본 정보를 표시하고 관리하는 컴포넌트
 */
const BasicInfo = ({ contract, fetchContract, isContractor = false, setPrimaryQuotationName }: Props) => {
  const { t } = useTranslation(["contract", "common"]);
  // 네비게이션 훅
  const navigate = usePartnerNavigate();

  // IFC 환경에서는 '이용자 출입내역' 기능을 활용하지 않아 버튼 숨김 처리 (PS-1744)
  const isIFC = getAppInfo().appType === "IFC";
  const { member } = useMemberInfoState();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sellerData = JSON.parse(decodeURIComponent(searchParams.get("data") || "{}"));
  // (무인증) 건물 상세정보 목록 api
  const { executeAsync: getBuildingsDetails } = useApiOperation(getBuildingsDetailsAsync, { noAuthenticationRequired: true });

  const { executeAsync: findSnapshot } = useApiOperation(getRefundInfo);

  // (무인증) 공통 미디어파일 목록 api
  const { executeAsync: getPublicMediaFiles } = useApiOperation(getPublicMediaFilesAsync, { noAuthenticationRequired: true, noHandleError: true });

  // 이용신청 취소 api
  const { executeAsync: patchContractCancel } = useApiOperation(patchContractCancelAsync, { noHandleError: true });

  // 계약 견적서 조회 api
  const { executeAsync: patchContractQuotations } = useApiOperation(getQuotationList);
  // 상품의 건물 데이터 중 공용공간 매핑을 위해 조회 API
  const { executeAsync: patchContractProduct } = useApiOperation(getProductAsync);

  // 건물 상세
  const [building, setBuilding] = useState<BuildingModel | null>(null);

  // 공간상품 대표 이미지
  const [primaryMediaFile, setPrimaryMediaFile] = useState<MediaFile>();
  // 신청/계약 취소 상태 변경 시
  const [cancelCheck, setCancelCheck] = useState(false);

  // 확인 취소 버튼 있는 확인 모달
  const [confirmModal, setConfirmModal] = useState<Modal>({ isOpen: false });

  // 확인 버튼만 있는 알림 모달
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });

  const [quotationCustomList, setQuotationCustomList] = useState<any[]>([]);

  const [refundInfo, setRefundInfo] = useState<any>(null);
  const [guideInfo, setGuideInfo] = useState<any>(null);

  const [product, setProduct] = useState<ProductModel>();

  // 계약예정 후의 상테 (신청접수 / 신청취소 / 신청확인 상태는 제외)
  const isAfterApplyContractStatus = useMemo(
    () =>
      ["CONTRACT_PLAN", "CONTRACT_ACCEPT", "USE_APPROVAL", "USE_PROGRESS", "USE_COMPLETE", "TERMINATE_RECEIVED", "TERMINATE_COMPLETE"].includes(
        contract?.contractStep || "",
      ),
    [contract?.contractStep],
  );

  // 임대 스케줄 정보
  const rentalSchedule = useMemo(() => {
    return contract?.scheduleList.find((schedule: any) => schedule.supplyType === "RENTAL");
  }, [contract]);

  // 권한이 유효한 상태 (이용중 / 해지접수) Boolean 리턴
  const isAuthorizedStatus = useMemo(() => ["USE_PROGRESS", "TERMINATE_RECEIVED"].includes(contract?.contractStep || ""), [contract]);
  const showTimeCourtCancelAction =
    isContractor &&
    contract?.spaceProductType === "TIME_COURT" &&
    ["CONTRACT_PLAN", "CONTRACT_ACCEPT", "USE_APPROVAL"].includes(contract?.contractStep!);
  const showContractorApplyCancelAction =
    isContractor &&
    contract?.spaceProductType !== "TIME_COURT" &&
    ["APPLY_RECEIVED", "APPLY_CONFIRM"].includes(contract?.contractStep || "") &&
    moment(contract?.contractEndTime).isAfter(moment());

  // 건물 주소
  const buildingAddress: string = useMemo(() => {
    if (!building) return "";
    const address: Address | undefined = (building?.addressList || [])[0];
    return address ? `${address?.address} ${address?.addressDetail}` : "";
  }, [building]);

  //계약 상품의 환불정보 조회
  const fetchFindInfo = useCallback(
    async (contractId: string) => {
      const _guideInfo = await findSnapshot({ contractId: contractId, domain: "product", attrName: "productNotice" } as any);
      if (_guideInfo.status === 200 && _guideInfo.data.data && _guideInfo.data.data.content && _guideInfo.data.data.content.length > 0) {
        setGuideInfo(_guideInfo.data.data.content[0]);
      }
      const _refundInfo = await findSnapshot({ contractId: contractId, domain: "product", attrName: "refundInfo" } as any);
      if (_refundInfo.status === 200 && _refundInfo.data.data && _refundInfo.data.data.content && _refundInfo.data.data.content.length > 0) {
        setRefundInfo(_refundInfo.data.data.content[0]);
      }
    },
    [findSnapshot],
  );

  //상품 조회
  const getContractProductData = useCallback(
    async (productId: string) => {
      const response = await patchContractProduct({ productId, memberNo: member?.memberNo });
      if (response.data.data.content) {
        return response.data.data.content;
      }
    },
    [patchContractProduct, member?.memberNo],
  );

  // 신청 취소
  const contractCancel = useCallback(async () => {
    if (!contract?.contractId) {
      throw Error("contract?.contractId is not found");
    }
    const { data } = await patchContractCancel({ contractId: contract?.contractId });
    if (data?.data?.contractId) {
      await fetchContract(String(contract.contractId));
      setAlertModal({ isOpen: true, message: t("detail.cancelCompleteMessage") });
    }
  }, [patchContractCancel, contract, fetchContract, t]);

  // Modal 확인 버튼 클릭
  const clickModalConfirm = useCallback(async () => {
    setConfirmModal({ isOpen: false });
    setCancelCheck(false);
    if (confirmModal.type === "CONTRACT_CANCEL") {
      await contractCancel();
    }
  }, [confirmModal, contractCancel]);

  // 건물 상세정보 조회
  const fetchBuildingsDetail = useCallback(
    async (buildingId: string | number) => {
      const { data } = await getBuildingsDetails({
        buildingIds: [buildingId],
      });
      if (data?.data?.content && (data?.data?.content || []).length > 0) {
        setBuilding((data?.data?.content || [])[0]?.building);
      }
    },
    [getBuildingsDetails],
  );

  // 공통 미디어파일 목록 조회
  const fetchPublicMediaFiles = useCallback(
    async (buildingId: string | number) => {
      const { data } = await getPublicMediaFiles({
        serviceId: buildingId,
        mediaServiceTypes: ["MEDIA_BUILDING"], // 건물 외관 ( 건물 대표 카테고리 )
      });

      if (data?.data?.content && (data?.data?.content || []).length > 0) {
        const _primaryMediaFile = data.data.content.find((v: MediaFile) => v.isPrimary === true);

        if (_primaryMediaFile) {
          setPrimaryMediaFile(_primaryMediaFile);
        }
      }
    },
    [getPublicMediaFiles],
  );

  // memberCancelableMinutes를 일/시간/분으로 변환
  const cancelableTimeText = useMemo(() => {
    if (contract?.memberCancelableMinutes === undefined || contract?.memberCancelableMinutes === null) {
      return "";
    }

    const totalMinutes = contract.memberCancelableMinutes;
    const days = Math.floor(totalMinutes / 1440);
    const remainingMinutes = totalMinutes % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(t("detail.durationDays", { count: days }));
    if (hours > 0) parts.push(t("detail.durationHours", { count: hours }));
    if (minutes > 0) parts.push(t("detail.durationMinutes", { count: minutes }));

    return parts.join(" ");
  }, [contract?.memberCancelableMinutes, t]);

  // 취소 버튼 타이틀 결정
  const cancelTitle = useMemo(() => {
    const contractStep = contract?.contractStep;

    // CONTRACT_PLAN (계약예정) 인 경우 → [취소] 버튼 출력
    if (contractStep === "CONTRACT_PLAN") {
      return "취소";
    }

    // CONTRACT_ACCEPT, USE_APPROVAL (계약체결, 이용승인) 일 때
    if (contractStep === "CONTRACT_ACCEPT" || contractStep === "USE_APPROVAL") {
      // isMemberCancelable = false 인 경우 → [취소불가] 버튼 출력
      if (contract?.isMemberCancelable === false) {
        return "취소불가";
      }

      // isMemberCancelable = true 인 경우
      if (contract?.isMemberCancelable === true) {
        if (contract?.contractStartTime && contract?.memberCancelableMinutes !== undefined) {
          // 취소가능 일시 = contractStartTime - memberCancelableMinutes
          const contractStartTime = moment(contract.contractStartTime);
          const cancelableTime = contractStartTime.clone().subtract(contract.memberCancelableMinutes, "minutes");
          const now = moment();

          // 현재일시가 취소가능 일시를 지나지 않은 경우 → [취소] 버튼 출력
          // 현재일시가 취소가능 일시를 지난 경우 → [취소불가] 버튼 출력
          if (now.isBefore(cancelableTime) || now.isSame(cancelableTime)) {
            return "취소";
          } else {
            return "취소불가";
          }
        }
        // contractStartTime이나 memberCancelableMinutes가 없는 경우 기본값
        return "취소";
      }
    }

    // 그 외 계약상태 인 경우 → [취소불가] 버튼 출력
    return "취소불가";
  }, [contract]);

  // 계약 취소 처리
  const changeContractCancel = useCallback(() => {
    //납부 완료 상태에서는 취소 요청 팝업이 열리고
    if (rentalSchedule && rentalSchedule.recentBill && rentalSchedule.recentBill.billPayStatus === "PAYMENT_SUCCESS") {
      //계약진행단계(이용승인) & 이용시작일시 기준 24시간 이내
      if (contract?.contractStep === "USE_PROGRESS" || contract?.contractStep === "USE_COMPLETE") {
        const am = _.cloneDeep(alertModal);
        am.isOpen = true;
        am.message = t("detail.cannotCancelStartedReservation");
        setAlertModal(am);
        return;
      }
      if (contract?.contractStep === "USE_APPROVAL") {
        const contractStartTime = moment(contract.contractStartTime);
        const today = moment();
        const divide = moment.duration(moment(contractStartTime).diff(today)).asHours();
        if (divide < 24) {
          return;
        }
      }
      setCancelCheck(true);
    } else {
      //납부 완료 상태가 아니면 신청 계약 취소 호출
      setConfirmModal({ isOpen: true, message: t("detail.cancelConfirmMessage"), type: "CONTRACT_CANCEL" });
    }
  }, [alertModal, contract, rentalSchedule, t]);

  // 계약 견적서 조회
  const contractQuotationList = useCallback(
    async (quotationList: any) => {
      //S2 계약의 견적서 id 정리 - 마지막 quotation만 사용
      let orderNumSorted = _.sortBy(quotationList, ["orderNum"]).reverse();
      const lastQuotation = orderNumSorted[0]; // 가장 큰 orderNum (마지막 quotation)

      if (!lastQuotation) return;

      const response: any = await patchContractQuotations({ quotationNo: lastQuotation.quotationNo });
      let quotationCustom: any = [];
      // 마지막 quotation의 itemList만 사용
      if (response.data.data.content && response.data.data.content.length > 0) {
        const lastQuotationData = response.data.data.content[0];
        lastQuotationData.quotationItemList.forEach((itemList: any) => {
          quotationCustom.push(itemList);
        });
      }

      quotationCustom.forEach(async (item: any) => {
        let products: any = {};
        //건물의 공용공간 조회
        products = await getContractProductData(item.productId!);
        setProduct(products);
        const fcs = makeCommonSpaceList(products);
        if (fcs.length > 0) {
          const fc = fcs.find((facility: any) => facility.id === item.buildingCommonFacilityId);
          if (fc) {
            item.facility = fc;
          }
        }
      });

      setPrimaryQuotationName(quotationCustom[0]);
      setQuotationCustomList(quotationCustom);

      //견적서의 공용공간으로 건물의 공용공간 중 매핑되는 것을 처리

      //
    },
    [getContractProductData, patchContractQuotations, setPrimaryQuotationName],
  );

  useEffect(() => {
    if (!contract) return;

    if (contract.spaceProductType !== "TIME_COURT") {
      if (contract.buildingId) {
        // 건물 상세정보 조회
        fetchBuildingsDetail(contract.buildingId);

        // 공통 미디어파일 목록 조회
        fetchPublicMediaFiles(contract.buildingId);
      }
    } else {
      contractQuotationList(contract.quotationList);
    }
    if (isContractor && contract.contractId) {
      fetchFindInfo(String(contract.contractId));
    }
  }, [contract, isContractor, fetchBuildingsDetail, fetchPublicMediaFiles, contractQuotationList, fetchFindInfo]);

  const findPrimaryImage = (quotation: any) => {
    let url = "";
    if (
      quotation.facility &&
      quotation.facility.mediaList &&
      Array.isArray(quotation.facility.mediaList) &&
      quotation.facility.mediaList.length > 0
    ) {
      const findPrimary = quotation.facility.mediaList.find((media: any) => media.isPrimary);
      if (findPrimary) {
        url = findPrimary.url;
      } else {
        url = quotation.facility.mediaList[0].url;
      }
    }
    return url;
  };

  console.log(contract);
  return (
    <>
      <section className="contract-main">
        <div className="contract-main__index-wrap">
          <p>{t("detail.basicInformation")}</p>
          <div className={`base-chip ${["USE_APPROVAL", "USE_PROGRESS"].includes(contract?.contractStep || "") ? "" : "color-gray"}`}>
            {changeStatusText(contract?.spaceProductType!, contract?.contractStep!)}
          </div>
        </div>
        <div className="base-section-layout__container">
          <div className="base-section-layout__wrap">
            <div className="base-section-layout-line value-priority">
              <p className="base-section-layout-index text-primary3">{t("detail.contractManageId")}</p>
              <div className="flex-row flex-center-end">
                <p className="base-section-layout-value">{contract?.contractManageId}</p>
                {showTimeCourtCancelAction && (
                  <div className="flex-center-end">
                    {cancelTitle !== "취소불가" ? (
                      <p className="base-section-layout-value ml10 text-primary3 cursor-pointer" onClick={() => changeContractCancel()}>
                        {t("detail.cancel")}
                      </p>
                    ) : (
                      <>
                        <p className="base-section-layout-value ml10 text-primary3">{t("detail.cancelUnavailable")}</p>
                        <Tooltip
                          title={t("detail.cancelUnavailableTooltipTitle")}
                          children={
                            <p>
                              {contract?.isMemberCancelable === false
                                ? t("detail.nonCancelableProduct")
                                : cancelableTimeText
                                ? t("detail.cancelableBeforeStart", { time: cancelableTimeText })
                                : t("detail.cancelableBeforeStartNoTime")}
                            </p>
                          }
                          position={"right"}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="base-section-layout-line">
              <p className="base-section-layout-index text-primary3">{t("detail.applicationDate")}</p>
              <p className="base-section-layout-value">{moment(contract?.createdDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM)}</p>
            </div>
          </div>
        </div>
        {contract?.spaceProductType !== "TIME_COURT" && (
          <div className="gray-card">
            <div className="gray-card__product">
              <ResizedImage
                className="minmax60"
                isUseSmallSize
                url={primaryMediaFile?.url || noimg}
                alt={t("detail.productImageAlt", { name: contract?.spaceProductName })}
              />
              <div className="gray-card__contents">
                <p className="gray-card__contents-title">{contract?.contractName ? `${contract.contractName}` : contract?.spaceProductName}</p>
                <p className="gray-card__contents-address">{buildingAddress}</p>
              </div>
            </div>
            <div className="gray-card__info">
              <div className="base-section-layout-line">
                <p className="base-section-layout-index text-primary3">{t("spaceRental.usagePeriod")}</p>
                <p className="base-section-layout-value">
                  {contract?.contractStartTime && moment(contract?.contractStartTime).format(ViewYmdFormat.YYYY_MM_DD_WEEK)} ~{" "}
                  {contract?.contractEndTime && moment(contract?.contractEndTime).format(ViewYmdFormat.YYYY_MM_DD_WEEK)}
                </p>
              </div>
            </div>
          </div>
        )}

        {Array.isArray(quotationCustomList) &&
          quotationCustomList?.map((quotation: any, index: number) => {
            return (
              <div className="gray-card" key={index}>
                <div className="gray-card__product">
                  <ResizedImage
                    className="minmax60"
                    isUseSmallSize
                    url={findPrimaryImage(quotation) || noimg}
                    alt={t("detail.commonSpaceImageAlt", { name: quotation?.facility?.facilityName })}
                  />
                  <div className="gray-card__contents">
                    <p className="gray-card__contents-title">{contract?.spaceProductName}</p>
                    <p className="gray-card__contents-address">{quotation?.facility?.facilityName!}</p>
                  </div>
                </div>
                <div className="gray-card__info">
                  <div className="base-section-layout-line">
                    <p className="base-section-layout-value text-primary3 pre-formatted text-left">
                      <>{formatDateTimeRange(String(quotation?.startDate!), String(quotation?.endDate!), "S2")}</>
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

        {/* 이용신청 내역 버튼 노출 */}
        {contract?.spaceProductType === "TIME_COURT" && //
          contract.memberNo === member?.memberNo &&
          contract.applicationBody?.formData && (
            <div className="flex-center mt20">
              <BaseButton
                title={t("detail.applicationDetails")}
                className="base-btn color-white "
                onClick={() => {
                  navigate(`/court/mypage/contracts/${contract?.contractId}/application/detail`, {
                    state: {
                      quotation: quotationCustomList[0],
                      applicationBody: contract?.applicationBody,
                      spaceProductName: contract?.spaceProductName,
                    },
                  });
                }}
              />
            </div>
          )}

        {contract?.spaceProductType !== "TIME_COURT" && isAfterApplyContractStatus && (
          <div
            className="mt20"
            style={{
              display: "grid",
              gap: "10px",
              gridTemplateColumns: "repeat(2, 2fr)",
            }}
          >
            <button
              type="button"
              className="base-btn color-white size-contract"
              disabled={!["CONTRACT_ACCEPT", "USE_APPROVAL", "USE_PROGRESS", "TERMINATE_RECEIVED"].includes(contract?.contractStep || "")}
              onClick={() => {
                // 이용자 목록 화면으로 이동
                navigate(`/court/mypage/contracts/${contract?.contractId}/users`, { state: { contractId: contract?.contractId } });
              }}
            >
              {t("spaceRental.userList")}
            </button>
            {!isIFC && (
              <button
                className="base-btn color-white size-contract"
                disabled={!isAuthorizedStatus}
                onClick={() =>
                  navigate(`/court/mypage/contracts/${contract?.contractId}/access-log`, {
                    state: { contractId: contract?.contractId },
                  })
                }
              >
                {t("spaceRental.userAccessHistory")}
              </button>
            )}
            <button
              className="base-btn color-white size-contract"
              disabled={!isAuthorizedStatus}
              onClick={() =>
                navigate(`/court/mypage/contracts/${contract?.contractId}/visitor/history`, {
                  state: { contractId: contract?.contractId },
                })
              }
            >
              {t("spaceRental.visitorInvitationHistory")}
            </button>
            {!isIFC && (
              <button
                className="base-btn color-white size-contract"
                disabled={!isAuthorizedStatus}
                onClick={() =>
                  navigate(`/court/mypage/contracts/${contract?.contractId}/facilityTime`, {
                    state: { contractId: contract?.contractId },
                  })
                }
              >
                {t("spaceRental.sharedSpaceUsageTime")}
              </button>
            )}
          </div>
        )}

        {/*  F/O 타입: 상태값  신청접수, 신청확인 상태이고 계약 종료 일시가 현재일보다 미래일때만 출력 */}
        {showContractorApplyCancelAction && (
          <div className="pt20">
            <button
              type="button"
              className="base-btn color-white size-small"
              onClick={() => {
                setConfirmModal({ isOpen: true, message: t("detail.cancelConfirmMessage"), type: "CONTRACT_CANCEL" });
              }}
            >
              {t("detail.cancelRequest")}
            </button>
          </div>
        )}
        {cancelCheck && (
          <BaseFullModal
            isOpen={cancelCheck}
            children={
              <ContractCancel
                refundInfo={refundInfo}
                recentBill={rentalSchedule?.recentBill!}
                onClose={() => setCancelCheck(false)}
                onClick={() => {
                  setConfirmModal({ isOpen: true, message: t("detail.cancelConfirmMessage"), type: "CONTRACT_CANCEL" });
                }}
              />
            }
          />
        )}
        {/* 확인 취소 버튼 있는 확인 모달 */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          btnLeftTitle={t("common:cancel")}
          btnRightTitle={t("common:confirm")}
          onClose={() => setConfirmModal({ isOpen: false })}
          onClick={() => clickModalConfirm()}
        >
          <p className="font18 font-weight-700">{confirmModal.message || ""}</p>
        </ConfirmModal>
        {/* 확인 버튼 있는 확인 모달 */}
        <ConfirmModal
          isOpen={alertModal.isOpen}
          btnRightTitle={t("common:confirm")}
          onClick={() => {
            setAlertModal({ isOpen: false, message: "" });
          }}
        >
          <p className="font18 font-weight-700">{alertModal.message || ""}</p>
        </ConfirmModal>
      </section>

      {/*  임시로 넣어놓은 승인 후 결제하기 UI > */}
      {contract?.spaceProductType === "TIME_COURT" && // 예약상품 이고
        product?.approvalType === "ADMIN" && // 관리자 승인 이고
        contract?.contractStep === "CONTRACT_PLAN" && ( // 예약대기 상태일 때
          <>
            <section className="contract-main top-divider py20">
              <div className="contract-main__index-wrap">
                <p
                  style={{
                    opacity: 0.3,
                    mask: "linear-gradient(to bottom, black 0%, black 50%, transparent 100%)",
                    WebkitMask: "linear-gradient(to bottom, black 0%, black 50%, transparent 100%)",
                  }}
                >
                  {t("detail.paymentInformation")}
                </p>
              </div>
              <div className="gray-card">
                <div className="gray-card__product">
                  <div className="gray-card__contents text-center">
                    <p className="gray-card__contents-title">{t("detail.paymentAvailableAfterApproval")}</p>
                    <p className="gray-card__contents-address" style={{ lineHeight: "1.5" }}>
                      {t("detail.reservationNotificationAfterApproval")}
                    </p>
                  </div>
                </div>
              </div>
            </section>
            <div className="base-floating-btn-wrap border-top ">
              <div className="base-floating-btn-wrap px0">
                <BaseButton title={t("detail.pay")} type="button" disabled />
              </div>
            </div>
          </>
        )}
    </>
  );
};

export default BasicInfo;
